import type { FC } from "react";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Icons } from "@/components/NavIcon";
import { logoutTBUserSession, logoutUri } from "@/helpers/user.helpers";
import { windowOpen } from "@/helpers/window.helpers";
import type { Role } from "@/routerConfig";
import { userHasPermission } from "@/routerConfig/router.helpers";
import { useAppSelector } from "@/storeHooks";
import DropdownMenu, { type Item } from "../DropdownMenu";

type ProfileMenuProps = {
  testId?: string;
};

type ItemWithRoles = Item & { roles?: Role[] };

const ProfileMenu: FC<ProfileMenuProps> = ({ testId = "profile-menu" }) => {
  const userRoles = useAppSelector((state) => state.user).roles;
  const navigate = useNavigate();
  const { logout } = useAuth0();

  const items = useMemo(() => {
    const itemArr: ItemWithRoles[] = [
      {
        id: "profile",
        icon: "AccountCircleIcon",
        label: "Profile",
        onClick: (handleClose) => {
          navigate("/profile");
          handleClose();
        },
      },
      {
        id: "homeSettings",
        icon: "SettingsApplicationsIcon",
        label: "Home Settings",
        roles: ["admin"],
        onClick: (handleClose) => {
          navigate("/settings/home");
          handleClose();
        },
      },
      {
        id: "help",
        icon: "HelpOutlineIcon",
        label: "Help",
        onClick: (handleClose) => {
          windowOpen("https://help.nimbus.arenko.group", "_blank");
          handleClose();
        },
      },
      {
        id: "logout",
        icon: "ExitToAppIcon",
        label: "Logout",
        onClick: () => {
          logoutTBUserSession().finally(() => {
            logout({ logoutParams: { returnTo: logoutUri() } });
          });
        },
      },
    ];

    return itemArr.filter(
      (item) =>
        item.roles === undefined || userHasPermission(userRoles, item.roles)
    );
  }, [logout, navigate, userRoles]);

  return (
    <DropdownMenu
      buttonLabel="Profile menu"
      buttonElement={<Icons.MoreVertIcon />}
      testId={testId}
      items={items}
    />
  );
};

export default ProfileMenu;
